import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import clsx from "clsx";
import {
  Bell, ChevronDown, LogOut, Menu, Moon, PanelLeftClose, PanelLeftOpen, Radio,
  Search, Sun, UserCog, X,
} from "lucide-react";
import { urlFoto } from "../lib/cadastros";
import { COR_SEV, rotuloEvento, severidade } from "../lib/eventos";
import { ha } from "../lib/format";
import type { Device, Evento } from "../lib/traccar";

/**
 * Casca da aplicação: barra lateral fixa + topo.
 *
 * A navegação saiu do topo para a lateral porque a lista cresceu — painel,
 * histórico, dez cadastros, conexão, conta — e porque é o que o operador
 * espera de um sistema de gestão. O topo ficou com o que é ação: buscar um
 * veículo, ver alertas, trocar o tema, sair.
 */
export interface ItemNav { to: string; label: string; icon: React.ElementType; }
export interface SecaoNav { titulo: string; itens: ItemNav[]; }

export function Shell({ secoes, marca, logo, usuario, eventos, devices,
                        dark, onTema, onSair, cheio, faixa, semMenu, children }: {
  secoes: SecaoNav[];
  marca: string;
  logo?: string | null;
  usuario?: { id?: number; name: string; email: string; foto?: string } | null;
  eventos: Evento[];
  devices: Map<number, Device>;
  dark: boolean;
  onTema: () => void;
  onSair: () => void;
  /** Página que ocupa a tela inteira (o painel): sem respiro e sem rolagem. */
  cheio?: boolean;
  /** Aviso fixo (cota, sessão compartilhada) — fica FORA da área rolável. */
  faixa?: ReactNode;
  /**
   * Esconde a barra lateral inteira e o botão que a abre.
   *
   * ⚠️ É para a sessão de LINK COMPARTILHADO, que existe para uma coisa só:
   * acompanhar um veículo no mapa. Um menu com Painel, Relatórios e Faturamento
   * ali seria um convite a telas que ou estão vazias ou devolvem 403.
   */
  semMenu?: boolean;
  children: ReactNode;
}) {
  const [aberta, setAberta] = useState(false);     // gaveta no celular
  // Recolhida: só os ícones. Fica gravado porque é preferência de quem opera
  // o dia todo — reabrir o navegador e reencontrar a tela larga importa.
  const [recolhida, setRecolhida] = useState(() => {
    try { return localStorage.getItem("menu") === "recolhido"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("menu", recolhida ? "recolhido" : "aberto"); }
    catch { /* navegação privada */ }
  }, [recolhida]);

  // Na página cheia o documento não rola: quem rola é o mapa e a lista, por
  // dentro. Sem travar aqui, o canvas do mapa estica a área de rolagem e a
  // aplicação inteira desliza para fora da tela ao girar a roda do mouse.
  useEffect(() => {
    // Só trava a rolagem onde o layout encaixa na janela (laptop pra cima).
    if (!cheio || !window.matchMedia("(min-width: 1024px)").matches) return;
    // O elemento de rolagem é o <html> (body e html têm height: 100%), então
    // travar só o body não adianta.
    const raiz = document.documentElement;
    const antesRaiz = raiz.style.overflow, antesBody = document.body.style.overflow;
    raiz.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => { raiz.style.overflow = antesRaiz; document.body.style.overflow = antesBody; };
  }, [cheio]);

  return (
    <div className="min-h-full xl:flex">
      {/* fundo escurecido quando a gaveta abre */}
      {aberta && (
        <div className="fixed inset-0 z-40 bg-ink-900/40 xl:hidden"
             onClick={() => setAberta(false)} role="presentation" />
      )}

      {!semMenu && (
      <aside className={clsx(
        "fixed inset-y-0 left-0 z-50 flex flex-col border-r border-ink-200 bg-white",
        "transition-[width,transform] duration-300 dark:border-ink-800 dark:bg-ink-900",
        "xl:translate-x-0", aberta ? "translate-x-0" : "-translate-x-full",
        recolhida ? "w-[290px] xl:w-[88px]" : "w-[290px]")}>
        <div className={clsx("flex h-16 items-center",
                             recolhida ? "justify-between px-4 xl:justify-center" : "justify-between px-6")}>
          <Marca marca={marca} logo={logo} so={recolhida} />
          <button onClick={() => setAberta(false)} aria-label="Fechar menu"
            className="rounded-lg p-1.5 text-ink-500 xl:hidden hover:bg-ink-100 dark:hover:bg-white/5">
            <X size={18} />
          </button>
        </div>

        <nav className={clsx("flex-1 overflow-y-auto pb-6", recolhida ? "px-3 xl:px-2.5" : "px-4")}>
          {secoes.map(secao => (
            <div key={secao.titulo} className="mb-6">
              <h2 className={clsx("mb-3 text-[11px] font-medium uppercase tracking-[0.08em] text-ink-400",
                                  recolhida ? "px-3 xl:text-center" : "px-3")}>
                {/* Recolhida, o título da seção vira um traço: o espaço não
                    comporta a palavra e sumir com ele apagaria a divisão. */}
                <span className={recolhida ? "xl:hidden" : ""}>{secao.titulo}</span>
                <span className={recolhida ? "hidden xl:inline" : "hidden"} aria-hidden>···</span>
              </h2>
              <ul className="flex flex-col gap-1">
                {secao.itens.map(({ to, label, icon: Icone }) => (
                  <li key={to}>
                    <NavLink to={to} end={to === "/"} onClick={() => setAberta(false)}
                      // O realce do item ativo é derivado da cor DO CLIENTE:
                      // um azul fixo aqui denunciaria o template por baixo.
                      style={({ isActive }) => isActive
                        ? { background: "color-mix(in srgb, var(--color-brand-tenant) 10%, transparent)" }
                        : undefined}
                      title={recolhida ? label : undefined}
                      className={({ isActive }) => clsx("item-menu",
                        recolhida && "xl:justify-center xl:px-0",
                        isActive
                          ? "text-[var(--color-brand-text)] dark:text-[var(--color-brand-tenant)]"
                          : "text-ink-700 hover:bg-ink-100 dark:text-ink-300 dark:hover:bg-white/5")}>
                      {({ isActive }) => (<>
                        <Icone size={18} strokeWidth={1.9} aria-hidden
                               className={clsx("shrink-0",
                                 isActive ? "" : "text-ink-500 dark:text-ink-400")} />
                        <span className={recolhida ? "xl:hidden" : ""}>{label}</span>
                      </>)}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </aside>
      )}

      <div className={clsx("flex min-w-0 flex-1 flex-col transition-[margin] duration-300",
                           cheio ? "h-screen" : "min-h-screen",
                           semMenu ? "" : recolhida ? "xl:ml-[88px]" : "xl:ml-[290px]")}>
        <header className="sticky top-0 z-30 border-b border-ink-200 bg-white/90 backdrop-blur
                           dark:border-ink-800 dark:bg-ink-900/90">
          <div className="flex h-16 items-center gap-3 px-4 md:px-6">
            {/* No celular abre a gaveta; no computador recolhe a barra.
                Sem menu, os dois somem — abrir uma gaveta vazia é pior que
                não ter botão. */}
            {semMenu && <Marca marca={marca} logo={logo} />}
            {!semMenu && (
            <button onClick={() => setAberta(true)} aria-label="Abrir menu"
              className="rounded-lg border border-ink-200 p-2.5 text-ink-500 t-fast hover:bg-ink-100
                         xl:hidden dark:border-ink-800 dark:hover:bg-white/5">
              <Menu size={18} />
            </button>
            )}
            {!semMenu && (
            <button onClick={() => setRecolhida(r => !r)}
              aria-label={recolhida ? "Expandir menu" : "Recolher menu"}
              aria-pressed={recolhida}
              className="hidden rounded-lg border border-ink-200 p-2.5 text-ink-500 t-fast
                         hover:bg-ink-100 xl:block dark:border-ink-800 dark:hover:bg-white/5">
              {recolhida ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
            </button>
            )}

            <BuscaVeiculos devices={devices} />

            <div className="ml-auto flex items-center gap-2">
              <Redondo rotulo="Alternar tema" onClick={onTema}>
                {dark ? <Sun size={18} /> : <Moon size={18} />}
              </Redondo>
              <Alertas eventos={eventos} devices={devices} />
              <MenuUsuario usuario={usuario} temConta={!semMenu} onSair={onSair} />
            </div>
          </div>
        </header>

        {faixa && <div className="shrink-0 px-4 pt-3 md:px-6 md:pt-4">{faixa}</div>}

        {/* Página cheia (o painel) ocupa a janela e não rola: quem manda no
            espaço é o mapa. O resto mantém o respiro e a largura máxima. */}
        <main className={cheio
          ? "min-h-0 flex-1 overflow-y-auto lg:overflow-hidden"
          : "mx-auto w-full max-w-[1680px] flex-1 p-4 md:p-6"}>
          {children}
        </main>
      </div>
    </div>
  );
}

function Marca({ marca, logo, so }: { marca: string; logo?: string | null; so?: boolean }) {
  const [falhou, setFalhou] = useState(false);
  // Logotipo novo merece uma nova chance: sem isto, uma falha antiga deixaria
  // o cabeçalho no nome para sempre.
  useEffect(() => { setFalhou(false); }, [logo]);
  // Recolhida, o logotipo do cliente daria lugar a uma tarja ilegível: fica só
  // a marca gráfica, com o nome no title.
  if (logo && !falhou && !so)
    return <img src={logo} alt={marca} onError={() => setFalhou(true)}
                className="h-8 max-w-[170px] object-contain" />;
  return (
    <span className="flex items-center gap-2.5" title={marca}>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white"
            style={{ background: "var(--color-brand-tenant)" }}>
        <Radio size={18} strokeWidth={2.2} aria-hidden />
      </span>
      <span className={clsx("truncate text-[15px] font-semibold text-ink-800 dark:text-white/90",
                            so && "xl:hidden")}>
        {marca}
      </span>
    </span>
  );
}

/** Cabeçalho de página: título e trilha, como o TailAdmin faz. */
export function Titulo({ titulo, trilha, acao }: {
  titulo: string; trilha?: string; acao?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
      <h1 className="text-xl font-semibold text-ink-800 dark:text-white/90">{titulo}</h1>
      <div className="flex items-center gap-3">
        {acao}
        {trilha && (
          <nav className="flex items-center gap-2 text-sm text-ink-500" aria-label="Trilha">
            <span>Início</span>
            <span className="text-ink-300">/</span>
            <span className="text-ink-800 dark:text-ink-200">{trilha}</span>
          </nav>
        )}
      </div>
    </div>
  );
}

function Redondo({ children, rotulo, ...p }:
  React.ButtonHTMLAttributes<HTMLButtonElement> & { rotulo: string }) {
  return (
    <button {...p} aria-label={rotulo} title={rotulo}
      className="relative flex h-11 w-11 items-center justify-center rounded-full border
                 border-ink-200 text-ink-500 t-fast hover:bg-ink-100 hover:text-ink-700
                 dark:border-ink-800 dark:text-ink-400 dark:hover:bg-white/5">
      {children}
    </button>
  );
}

/**
 * Busca de veículo no topo.
 *
 * Não é enfeite: navega para o painel com o veículo selecionado. Caixa de
 * busca que não busca é a primeira coisa que denuncia template colado.
 */
function BuscaVeiculos({ devices }: { devices: Map<number, Device> }) {
  const [termo, setTermo] = useState("");
  const [aberto, setAberto] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);
  const navegar = useNavigate();

  const achados = useMemo(() => {
    const t = termo.trim().toLowerCase();
    if (!t) return [];
    return [...devices.values()]
      .filter(d => d.name.toLowerCase().includes(t) || d.uniqueId.toLowerCase().includes(t))
      .slice(0, 6);
  }, [termo, devices]);

  useEffect(() => {
    const fora = (e: MouseEvent) => {
      if (caixa.current && !caixa.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, []);

  // ⌘K / Ctrl+K: atalho que quem usa sistema de gestão o dia todo procura.
  const campo = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const atalho = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault(); campo.current?.focus();
      }
    };
    window.addEventListener("keydown", atalho);
    return () => window.removeEventListener("keydown", atalho);
  }, []);

  const ir = (id: number) => {
    setTermo(""); setAberto(false);
    navegar(`/mapa?veiculo=${id}`);
  };

  return (
    <div ref={caixa} className="relative hidden w-full max-w-md sm:block">
      <Search size={17} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2
                                   text-ink-400" aria-hidden />
      <input ref={campo} value={termo} placeholder="Buscar veículo por nome ou identificador…"
        onChange={e => { setTermo(e.target.value); setAberto(true); }}
        onFocus={() => setAberto(true)}
        aria-label="Buscar veículo"
        className="campo pl-11 pr-16" />
      <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2
                      rounded-md border border-ink-200 bg-ink-50 px-1.5 py-0.5 text-[11px]
                      text-ink-500 md:block dark:border-ink-800 dark:bg-white/5">
        ⌘K
      </kbd>

      {aberto && termo && (
        <div className="absolute left-0 right-0 top-[52px] z-40 overflow-hidden rounded-xl border
                        border-ink-200 bg-white shadow-[var(--shadow-lg)] dark:border-ink-800
                        dark:bg-ink-900">
          {achados.length ? achados.map(d => (
            <button key={d.id} onClick={() => ir(d.id)}
              className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left t-fast
                         hover:bg-ink-50 dark:hover:bg-white/5">
              <span className="text-sm font-medium text-ink-800 dark:text-ink-100">{d.name}</span>
              <span className="tnum font-mono text-[11px] text-ink-400">{d.uniqueId}</span>
            </button>
          )) : (
            <p className="px-4 py-4 text-sm text-ink-500">Nenhum veículo com esse termo.</p>
          )}
        </div>
      )}
    </div>
  );
}

function Alertas({ eventos, devices }: { eventos: Evento[]; devices: Map<number, Device> }) {
  const [aberto, setAberto] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);
  const navegar = useNavigate();
  const criticos = eventos.filter(e => severidade(e.type) === "critico").length;

  useEffect(() => {
    const fora = (e: MouseEvent) => {
      if (caixa.current && !caixa.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, []);

  return (
    <div ref={caixa} className="relative">
      <Redondo rotulo="Alertas" onClick={() => setAberto(a => !a)}>
        <Bell size={18} />
        {eventos.length > 0 && (
          <span className={clsx("absolute right-2.5 top-2.5 h-2.5 w-2.5 rounded-full",
                                "ring-2 ring-white dark:ring-ink-900")}
                style={{ background: criticos ? "var(--color-critical)" : "var(--color-warning)" }}
                aria-hidden />
        )}
      </Redondo>

      {aberto && (
        <div className="absolute right-0 top-14 z-40 w-[340px] overflow-hidden rounded-xl border
                        border-ink-200 bg-white shadow-[var(--shadow-lg)] dark:border-ink-800
                        dark:bg-ink-900">
          <header className="flex items-center justify-between border-b border-ink-200 px-4 py-3
                             dark:border-ink-800">
            <h3 className="text-sm font-semibold text-ink-800 dark:text-white/90">Alertas</h3>
            <span className="tnum text-xs text-ink-500">{eventos.length} em 24 h</span>
          </header>
          <div className="max-h-[320px] overflow-y-auto">
            {eventos.slice(0, 8).map(e => {
              const sev = severidade(e.type);
              return (
                <button key={e.id}
                  onClick={() => { setAberto(false); navegar(`/mapa?veiculo=${e.deviceId}`); }}
                  className="flex w-full items-start gap-3 border-b border-ink-100 px-4 py-3 text-left
                             t-fast last:border-0 hover:bg-ink-50 dark:border-ink-800/60
                             dark:hover:bg-white/5">
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full"
                        style={{ background: COR_SEV[sev] }} aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-ink-800 dark:text-ink-100">
                      {rotuloEvento(e.type, typeof e.attributes?.alarm === "string"
                                              ? e.attributes.alarm : undefined)}
                    </span>
                    <span className="block truncate text-xs text-ink-500">
                      {devices.get(e.deviceId)?.name ?? `#${e.deviceId}`} · {ha(e.eventTime)}
                    </span>
                  </span>
                </button>
              );
            })}
            {!eventos.length && (
              <p className="px-4 py-8 text-center text-sm text-ink-500">
                Nenhum alerta nas últimas 24 horas.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MenuUsuario({ usuario, temConta = true, onSair }: {
  usuario?: { id?: number; name: string; email: string; foto?: string } | null;
  /** Sessão de link compartilhado não tem cadastro — logo, não tem conta. */
  temConta?: boolean;
  onSair: () => void;
}) {
  const [aberto, setAberto] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);
  const navegar = useNavigate();

  useEffect(() => {
    const fora = (e: MouseEvent) => {
      if (caixa.current && !caixa.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, []);

  const iniciais = (usuario?.name ?? "?").split(/\s+/).slice(0, 2)
    .map(p => p[0]?.toUpperCase() ?? "").join("");
  // Sessão sem id (link compartilhado) não tem cadastro para ter foto.
  const foto = usuario?.id ? urlFoto("users", { id: usuario.id, foto: usuario.foto }) : null;

  return (
    <div ref={caixa} className="relative">
      <button onClick={() => setAberto(a => !a)} aria-expanded={aberto}
        className="flex items-center gap-2 rounded-full py-1 pl-1 pr-2 t-fast hover:bg-ink-100
                   dark:hover:bg-white/5">
        <span className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full
                         text-xs font-semibold text-[var(--color-on-brand)]"
              style={{ background: "var(--color-brand-tenant)" }}>
          {foto
            ? <img src={foto} alt="" className="h-full w-full object-cover" />
            : iniciais}
        </span>
        <span className="hidden text-sm font-medium text-ink-700 sm:block dark:text-ink-200">
          {usuario?.name?.split(/\s+/)[0] ?? "Conta"}
        </span>
        <ChevronDown size={15} className="text-ink-400" aria-hidden />
      </button>

      {aberto && (
        <div className="absolute right-0 top-14 z-40 w-60 overflow-hidden rounded-xl border
                        border-ink-200 bg-white shadow-[var(--shadow-lg)] dark:border-ink-800
                        dark:bg-ink-900">
          <div className="border-b border-ink-200 px-4 py-3 dark:border-ink-800">
            <p className="truncate text-sm font-medium text-ink-800 dark:text-white/90">
              {usuario?.name ?? "—"}
            </p>
            <p className="truncate text-xs text-ink-500">{usuario?.email ?? ""}</p>
          </div>
          {/* ⚠️ Numa sessão por link, "Minha conta" leva a uma tela que não
              existe para ela: o usuário é temporário, sem cadastro para editar
              nem senha para trocar. */}
          {temConta && (
            <button onClick={() => { setAberto(false); navegar("/conta"); }}
              className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-ink-700
                         t-fast hover:bg-ink-50 dark:text-ink-200 dark:hover:bg-white/5">
              <UserCog size={16} className="text-ink-500" /> Minha conta
            </button>
          )}
          <button onClick={onSair}
            className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-ink-700
                       t-fast hover:bg-ink-50 dark:text-ink-200 dark:hover:bg-white/5">
            <LogOut size={16} className="text-ink-500" /> Sair
          </button>
        </div>
      )}
    </div>
  );
}
